import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { isMobile } from "react-device-detect";
import { Alert } from '../Alert';



const EyeSection = styled.div`
  width: 49%;
  background-color: #f5f5f5;
  padding: 5px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Divider = styled.hr`
  border: none;
  border-left: 1px solid #ddd;
  height: auto;
  margin: 0 20px;
`;

const SectionTitle = styled.h2`
  color: ${({ color }) => color || '#333'};
  margin-bottom: 20px;
`;

const PrescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin: 20px 0;
`;

const TableHeader = styled.th`
  padding: 12px 20px;
  background-color: ${({ backGroundColour }) => backGroundColour};
  color: ${({ fontColour }) => fontColour};
  text-align: center;
  border-radius: 3px;
`;

const TableCell = styled.td`
  padding: 12px 20px;
  border: 1px solid #ddd;
  text-align: center;
`;

const SelectInput = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background: #fff;
  width: 100%;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const FieldContainer = styled.div`
  width: 100%;
  padding: 1px;
`;

const Label = styled.label`
  display: ${({ show }) => (show ? 'block' : 'none')};
  margin-bottom: 5px;
  font-Size: 12px;
  color: #555;
`;

const SelectInputStyled = styled.select`
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: ${props => props.missing ? '#ffdddd' : '#fff'};
  width: 100%;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const generateOptions = (min, max, step) => {
  const options = [];
  for (let value = min; value <= max; value = parseFloat((value + step).toFixed(2))) {
    options.push({ label: value.toFixed(2), value: value.toFixed(2) });
  }
  return options;
};

const RangeSelect = ({ label, range, disabled, onChange, value, showLabel = true, required = false, missing = false, fixedDecimalPlaces = 2 }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (range && range.Min !== undefined && range.Max !== undefined && range.Step !== undefined) {
      const generatedOptions = [];
      for (let val = range.Min; val <= range.Max; val = parseFloat((val + range.Step).toFixed(fixedDecimalPlaces))) {
        generatedOptions.push(val.toFixed(fixedDecimalPlaces));
      }
      setOptions(generatedOptions);
    }
  }, [range]);

  return (
    <FieldContainer>
      <Label
        show={showLabel}>
        {label}
        {required && <span style={{ color: '#880808' }}> *</span>}
      </Label>
      <SelectInputStyled missing={missing} disabled={disabled} onChange={(e) => onChange(e.target.value)} value={value}>
        <option value="">Select</option>
        {options.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </SelectInputStyled>
    </FieldContainer>
  );
};

const DirectionSelect = ({ label, directions, disabled, onChange, value, showLabel = true }) => (
  <FieldContainer>
    <Label show={showLabel}>{label}</Label>
    <SelectInputStyled disabled={disabled} onChange={(e) => onChange(e.target.value)} value={value}>
      <option value="">Select</option>
      {directions.map((direction, index) => (
        <option key={index} value={direction}>{direction}</option>
      ))}
    </SelectInputStyled>
  </FieldContainer>
);

export const PrescriptionTableRedesignTwoColumn = ({ colours, data, orderDetails, handleEnteredValues, selectedLensType = null, SVType = null, missingFields = [] }) => {
  return (
    <>
      {['RightEye', 'LeftEye'].map((eye) => (
        <>
          {!isMobile && <Divider />}
          <EyeSection key={eye}>
            <SectionTitle color={colours.sidebar}>
              {eye === 'RightEye' ? 'Right Eye' : 'Left Eye'}
            </SectionTitle>


            <RangeSelect
              label="Sph"
              range={data?.[eye]?.Sphere?.Range || []}
              disabled={!data?.[eye]?.Sphere?.Range}
              onChange={(value) => {
                const eyeKey = eye === 'RightEye' ? 'rightEye' : 'leftEye';

                handleEnteredValues(`prescription.${eyeKey}.sphere`, value);

                if (orderDetails?.standardLensPrescription) {
                  if (!orderDetails?.standardLensPrescription[eyeKey]?.sphere || orderDetails?.standardLensPrescription[eyeKey]?.sphere === "0.00") {
                    handleEnteredValues(`standardLensPrescription.${eyeKey}.sphere`, value);
                  }
                }
              }}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].sphere}
              required={selectedLensType === "Single Vision" || (selectedLensType === "Bifocal" || selectedLensType === "Progressive")}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Sphere`]}
            />

            <RangeSelect
              label="Cyl"
              range={data?.[eye]?.Cylinder?.Range || []}
              disabled={!data?.[eye]?.Cylinder?.Range}
              onChange={(value) => {
                const eyeKey = eye === 'RightEye' ? 'rightEye' : 'leftEye';

                handleEnteredValues(`prescription.${eyeKey}.cylinder`, value);

                if (!orderDetails?.standardLensPrescription[eyeKey]?.cylinder || orderDetails?.standardLensPrescription[eyeKey]?.cylinder === "0.00") {
                  handleEnteredValues(`standardLensPrescription.${eyeKey}.cylinder`, value);
                }
              }}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].cylinder}
              required={selectedLensType === "Single Vision" || (selectedLensType === "Bifocal" || selectedLensType === "Progressive")}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Cylinder`]}
            />

            <RangeSelect
              label="Axis"
              range={data?.[eye]?.Axis?.Range || []}
              disabled={!data?.[eye]?.Axis?.Range}
              onChange={(value) => {
                const eyeKey = eye === 'RightEye' ? 'rightEye' : 'leftEye';

                handleEnteredValues(`prescription.${eyeKey}.axis`, value);

                if (!orderDetails?.standardLensPrescription[eyeKey]?.axis || orderDetails?.standardLensPrescription[eyeKey]?.axis === "0.00" || orderDetails?.standardLensPrescription[eyeKey]?.axis === "0") {
                  handleEnteredValues(`standardLensPrescription.${eyeKey}.axis`, value);
                }
              }}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].axis}
              required={selectedLensType === "Single Vision" || (selectedLensType === "Bifocal" || selectedLensType === "Progressive")}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Axis`]}
              fixedDecimalPlaces={0}
            />

            <RangeSelect
              label="Add"
              range={data?.[eye]?.Add || []}
              disabled={!data?.[eye]?.Add}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.add`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].add}
              required={selectedLensType === "Bifocal" || selectedLensType === "Progressive"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Add`]}
            />

            <RangeSelect
              label="Heights (mm)"
              range={data?.[eye]?.Heights || []}
              disabled={!data?.[eye]?.Heights}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.heights`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].heights}
              required={selectedLensType === "Bifocal" || selectedLensType === "Progressive"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Heights`]}
            />

            <RangeSelect
              label="H Prism"
              range={data?.[eye]?.HPrism?.Range || []}
              disabled={!data?.[eye]?.HPrism?.Range}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrism`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].hPrism}
            />

            <DirectionSelect
              label="H Dir"
              directions={data?.[eye]?.HPrism?.Direction || []}
              disabled={!data?.[eye]?.HPrism?.Direction}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrismDirection`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].hPrismDirection}
            />

            <RangeSelect
              label="V Prism"
              range={data?.[eye]?.VPrism?.Range || []}
              disabled={!data?.[eye]?.VPrism?.Range}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrism`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].vPrism}
            />

            <DirectionSelect
              label="V Dir"
              directions={data?.[eye]?.VPrism?.Direction || []}
              disabled={!data?.[eye]?.VPrism?.Direction}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrismDirection`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].vPrismDirection}
            />

            <RangeSelect
              label="Distance OC's"
              range={data?.[eye]?.DistanceOC || []}
              disabled={!data?.[eye]?.DistanceOC}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.distanceOC`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].distanceOC}
              required={(selectedLensType === "Bifocal" || selectedLensType === "Progressive") || selectedLensType === "Single Vision" && SVType !== "Intermediate"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}DistanceOC`]}
            />

            <RangeSelect
              label="Read OC's"
              range={data?.[eye]?.ReadOC || []}
              disabled={!data?.[eye]?.ReadOC}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.readOC`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].readOC}
              required={(selectedLensType === "Bifocal" || selectedLensType === "Progressive") || selectedLensType === "Single Vision" && SVType !== "Intermediate"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}ReadOC`]}
            />

            <RangeSelect
              label="Intermediate OC's"
              range={data?.[eye]?.IntermediateOC || []}
              disabled={!data?.[eye]?.IntermediateOC}
              onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.intermediateOC`, value)}
              value={orderDetails.prescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].intermediateOC}
              required={selectedLensType === "Single Vision" && SVType === "Intermediate"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}IntermediateOC`]}
            />

          </EyeSection>
        </>
      ))}

    </>
  );
};

export const StandardPrescriptionTableRedesignTwoColumn = ({ colours, data, orderDetails, handleEnteredValues, selectedLensType = null, SVType = null, missingFields = [] }) => {
  return (
    <>
      {['RightEye', 'LeftEye'].map((eye) => (
        <>
          {!isMobile && <Divider />}
          <EyeSection key={eye}>
            <SectionTitle color={colours.sidebar}>
              {eye === 'RightEye' ? 'Right Eye' : 'Left Eye'}
            </SectionTitle>


            <RangeSelect
              label="Sph"
              range={data?.[eye]?.Sphere?.Range || []}
              disabled={!data?.[eye]?.Sphere?.Range}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.sphere`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].sphere}
              required={selectedLensType === "Single Vision" || (selectedLensType === "Bifocal" || selectedLensType === "Progressive")}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Sphere`]}
            />

            <RangeSelect
              label="Cyl"
              range={data?.[eye]?.Cylinder?.Range || []}
              disabled={!data?.[eye]?.Cylinder?.Range}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.cylinder`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].cylinder}
              required={selectedLensType === "Single Vision" || (selectedLensType === "Bifocal" || selectedLensType === "Progressive")}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Cylinder`]}
            />

            <RangeSelect
              label="Axis"
              range={data?.[eye]?.Axis?.Range || []}
              disabled={!data?.[eye]?.Axis?.Range}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.axis`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].axis}
              required={selectedLensType === "Single Vision" || (selectedLensType === "Bifocal" || selectedLensType === "Progressive")}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Axis`]}
              fixedDecimalPlaces={0}
            />

            <RangeSelect
              label="Add"
              range={data?.[eye]?.Add || []}
              disabled={!data?.[eye]?.Add}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.add`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].add}
              required={selectedLensType === "Bifocal" || selectedLensType === "Progressive"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Add`]}
            />

            <RangeSelect
              label="Heights (mm)"
              range={data?.[eye]?.Heights || []}
              disabled={!data?.[eye]?.Heights}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.heights`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].heights}
              required={selectedLensType === "Bifocal" || selectedLensType === "Progressive"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}Heights`]}
            />

            <RangeSelect
              label="H Prism"
              range={data?.[eye]?.HPrism?.Range || []}
              disabled={!data?.[eye]?.HPrism?.Range}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrism`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].hPrism}
            />

            <DirectionSelect
              label="H Dir"
              directions={data?.[eye]?.HPrism?.Direction || []}
              disabled={!data?.[eye]?.HPrism?.Direction}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrismDirection`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].hPrismDirection}
            />

            <RangeSelect
              label="V Prism"
              range={data?.[eye]?.VPrism?.Range || []}
              disabled={!data?.[eye]?.VPrism?.Range}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrism`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].vPrism}
            />

            <DirectionSelect
              label="V Dir"
              directions={data?.[eye]?.VPrism?.Direction || []}
              disabled={!data?.[eye]?.VPrism?.Direction}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrismDirection`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].vPrismDirection}
            />

            <RangeSelect
              label="Distance OC's"
              range={data?.[eye]?.DistanceOC || []}
              disabled={!data?.[eye]?.DistanceOC}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.distanceOC`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].distanceOC}
              required={(selectedLensType === "Bifocal" || selectedLensType === "Progressive") || selectedLensType === "Single Vision" && SVType !== "Intermediate"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}DistanceOC`]}
            />

            <RangeSelect
              label="Read OC's"
              range={data?.[eye]?.ReadOC || []}
              disabled={!data?.[eye]?.ReadOC}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.readOC`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].readOC}
              required={(selectedLensType === "Bifocal" || selectedLensType === "Progressive") || selectedLensType === "Single Vision" && SVType !== "Intermediate"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}ReadOC`]}
            />

            <RangeSelect
              label="Intermediate OC's"
              range={data?.[eye]?.IntermediateOC || []}
              disabled={!data?.[eye]?.IntermediateOC}
              onChange={(value) => handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.intermediateOC`, value)}
              value={orderDetails.standardLensPrescription[eye === 'RightEye' ? 'rightEye' : 'leftEye'].intermediateOC}
              required={selectedLensType === "Single Vision" && SVType === "Intermediate"}
              missing={missingFields[`${eye === 'RightEye' ? 'Right' : 'Left'}IntermediateOC`]}
            />

          </EyeSection>
        </>
      ))}

    </>
  );
};

export const ReadonlyPrescriptionTable = ({ colours, data }) => {
  return (
    <>
      {['rxr', 'rxl'].map((eye) => (
        <>
          {!isMobile && <Divider />}
          <EyeSection key={eye}>
            <SectionTitle color={colours.sidebar}>
              {eye === 'rxr' ? 'Right Eye' : 'Left Eye'}
            </SectionTitle>

            <Label show={true}>Sph</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}sph`]}>
              <option value={data[`${eye}sph`]}>{data[`${eye}sph`]}</option>
            </SelectInputStyled>

            <Label show={true}>Cyl</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}cyl`]}>
              <option value={data[`${eye}cyl`]}>{data[`${eye}cyl`]}</option>
            </SelectInputStyled>

            <Label show={true}>Axis</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}axis`]}>
              <option value={data[`${eye}axis`]}>{data[`${eye}axis`]}</option>
            </SelectInputStyled>

            <Label show={true}>Add</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}add`]}>
              <option value={data[`${eye}add`]}>{data[`${eye}add`]}</option>
            </SelectInputStyled>

            <Label show={true}>Heights (mm)</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}hgt`]}>
              <option value={data[`${eye}hgt`]}>{data[`${eye}hgt`]}</option>
            </SelectInputStyled>

            <Label show={true}>H Prism</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}prismhor`]}>
              <option value={data[`${eye}prismhor`]}>{data[`${eye}prismhor`]}</option>
            </SelectInputStyled>

            <Label show={true}>H Dir</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}prismhordir`]}>
              <option value={data[`${eye}prismhordir`]}>{data[`${eye}prismhordir`]}</option>
            </SelectInputStyled>

            <Label show={true}>V Prism</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}prismvert`]}>
              <option value={data[`${eye}prismvert`]}>{data[`${eye}prismvert`]}</option>
            </SelectInputStyled>

            <Label show={true}>V Dir</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}prismvertdir`]}>
              <option value={data[`${eye}prismvertdir`]}>{data[`${eye}prismvertdir`]}</option>
            </SelectInputStyled>

            <Label show={true}>{data[`${eye}doc`] === data[`${eye}roc`] ? `OC's` : `Distance OC's`}</Label>
            <SelectInputStyled
              disabled={true}
              value={data[`${eye}doc`]}>
              <option value={data[`${eye}doc`]}>{data[`${eye}doc`]}</option>
            </SelectInputStyled>

            {(data[`${eye}doc`] !== data[`${eye}roc`]) &&
              <>
                <Label show={true}>Read OC's</Label>
                <SelectInputStyled
                  disabled={true}
                  value={data[`${eye}roc`]}>
                  <option value={data[`${eye}roc`]}>{data[`${eye}roc`]}</option>
                </SelectInputStyled>
              </>}
          </EyeSection>
        </>
      ))}

    </>
  );
};


const PrescriptionTable = ({ colours, data, orderDetails, handleEnteredValues }) => {
  if (!data) return null;

  return (
    <PrescriptionContainer>
      <Table>
        <thead>
          <tr>
            <TableHeader backGroundColour={colours.sidebar} />
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Sph</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Cyl</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Axis</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Add</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Heights (mm)</TableHeader>
          </tr>
        </thead>
        <tbody>
          {['RightEye', 'LeftEye'].map((eye) => (
            <tr key={eye}>
              <TableCell>{eye === 'RightEye' ? 'Right' : 'Left'}</TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.Sphere?.Range}
                  disabled={!data[eye]?.Sphere?.Range}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.sphere`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].sphere}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.Cylinder?.Range}
                  disabled={!data[eye]?.Cylinder?.Range}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.cylinder`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].cylinder}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.Axis?.Range}
                  disabled={!data[eye]?.Axis?.Range}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.axis`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].axis}
                  fixedDecimalPlaces={0}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.Add}
                  disabled={!data[eye]?.Add}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.add`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].add}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.Heights}
                  disabled={!data[eye]?.Heights}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.heights`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].heights}
                />
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>

      <Table>
        <thead>
          <tr>
            <TableHeader backGroundColour={colours.sidebar} />
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>H Prism</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>H Dir</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>V Prism</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>V Dir</TableHeader>
          </tr>
        </thead>
        <tbody>
          {['RightEye', 'LeftEye'].map((eye) => (
            <tr key={eye}>
              <TableCell>{eye === 'RightEye' ? 'Right' : 'Left'}</TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.HPrism?.Range}
                  disabled={!data[eye]?.HPrism?.Range}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrism`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].hPrism}
                />
              </TableCell>
              <TableCell>
                <DirectionSelect
                  directions={data[eye]?.HPrism?.Direction || []}
                  disabled={!data[eye]?.HPrism?.Direction}
                  onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrismDirection`, value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].hPrismDirection}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.VPrism?.Range}
                  disabled={!data[eye]?.VPrism?.Range}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrism`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].vPrism}
                />
              </TableCell>
              <TableCell>
                <DirectionSelect
                  directions={data[eye]?.VPrism?.Direction || []}
                  disabled={!data[eye]?.VPrism?.Direction}
                  onChange={(value) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrismDirection`, value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].vPrismDirection}
                />
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>

      <Table>
        <thead>
          <tr>
            <TableHeader backGroundColour={colours.sidebar} />
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Distance OC's</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Read OC's</TableHeader>
            <TableHeader backGroundColour={colours.sidebar} fontColour={colours.font}>Intermediate OC's</TableHeader>
          </tr>
        </thead>
        <tbody>
          {['RightEye', 'LeftEye'].map((eye) => (
            <tr key={eye}>
              <TableCell>{eye === 'RightEye' ? 'Right' : 'Left'}</TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.DistanceOC}
                  disabled={!data[eye]?.DistanceOC}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.distanceOC`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].distanceOC}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.ReadOC}
                  disabled={!data[eye]?.ReadOC}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.readOC`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].readOC}
                />
              </TableCell>
              <TableCell>
                <RangeSelect
                  range={data[eye]?.IntermediateOC}
                  disabled={!data[eye]?.IntermediateOC}
                  onChange={(event) => handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.intermediateOC`, event.target.value)}
                  value={orderDetails.prescription[`${eye === 'RightEye' ? 'rightEye' : 'leftEye'}`].intermediateOC}
                />
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </PrescriptionContainer>
  );
};

export default PrescriptionTable;
